import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { I18n, translate } from '@td/shared_utils';
import Autosuggest from 'react-autosuggest';
import { useLazyQuery } from '@apollo/client';
import { get } from 'lodash';
import { TdWindow } from '@td/utils';

import useDebouncedAction from '../../lib/hooks/useDebouncedAction';
import useCachedQueryData from '../../lib/hooks/useCachedQueryData';
import DRUGS_SEARCH_QUERY from '../queries/drugs-search.graphql';
import { SearchIcon, CloseIcon as ClearIcon } from '../../icons';
import { formattedDrugName } from '../utils/drug';

const SEARCH_DEBOUNCE_INTERVAL = 300;
const SEARCH_TERM_MIN_LENGTH = 3;

const MedicationAutocomplete = ({ initialValue, redirectOnClear }) => {
  const [search, setSearch] = useState(initialValue || '');

  const [searchDrugs, { data }] = useLazyQuery(DRUGS_SEARCH_QUERY, { fetchPolicy: 'network-only' });
  const [cachedResults, setCachedResults] = useCachedQueryData(data);

  const handleSearch = useCallback(
    ({ value: searchTerm }) => {
      if (searchTerm && searchTerm.length >= SEARCH_TERM_MIN_LENGTH) {
        searchDrugs({ variables: { term: searchTerm } });
      }
    },
    [searchDrugs]
  );

  const [debouncedSearch] = useDebouncedAction(handleSearch, SEARCH_DEBOUNCE_INTERVAL);

  const redirectToFormularyDetails = medication => {
    const queryParams = { search_term: formattedDrugName(medication.drugName, medication.specificity) };

    TdWindow.redirect(
      `/medication_formularies/${medication.drugId}?${new window.URLSearchParams(queryParams).toString()}`
    );
  };

  const clearInput = () => {
    if (redirectOnClear) {
      TdWindow.redirect(`/medication_formularies`);
    } else {
      setSearch('');
    }
  };

  return (
    <section className="search">
      <h4>
        <I18n scope="medication_formularies.search" text="title" />
      </h4>
      <div className="input-container">
        <SearchIcon className="input-icon" />
        {search && <ClearIcon width={14} height={14} className="input-icon clear-icon" onClick={clearInput} />}
        <Autosuggest
          id="medication-autosuggest"
          suggestions={get(cachedResults, 'drugsSearch') || []}
          getSuggestionValue={suggestion => formattedDrugName(suggestion.drugName, suggestion.specificity)}
          renderSuggestion={suggestion => formattedDrugName(suggestion.drugName, suggestion.specificity)}
          focusInputOnSuggestionClick={false}
          onSuggestionsFetchRequested={debouncedSearch}
          onSuggestionsClearRequested={() => setCachedResults(null)}
          onSuggestionSelected={(_event, { suggestion }) => {
            redirectToFormularyDetails(suggestion);
          }}
          inputProps={{
            onChange: (_event, { newValue }) => setSearch(newValue),
            value: search,
            placeholder: translate(null, 'medication_formularies.search', 'placeholder')
          }}
        />
      </div>
    </section>
  );
};

MedicationAutocomplete.propTypes = {
  initialValue: PropTypes.string,
  redirectOnClear: PropTypes.bool
};

MedicationAutocomplete.defaultProps = {
  initialValue: '',
  redirectOnClear: false
};

export default MedicationAutocomplete;
